import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";

import { PageBackButton } from "@/core/components/shared";
import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreatePostingTemplate } from "@/features/ledger/api/use-create-posting-template";
import type {
  PostingTemplateDto,
  PostingTemplateLineDto,
} from "@/features/ledger/api/dto";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import {
  createPostingTemplateSchema,
  type PostingTemplateFormValues,
} from "@/features/ledger/schemas/posting-template.schema";
import {
  ACCOUNT_ROLES,
  AMOUNT_BASIS,
  POSTING_DIRECTIONS,
} from "@/features/shared/api/enums";
import {
  useAccountSelectOptions,
  useProductCodeSelectOptions,
  useProviderSelectOptions,
} from "@/features/shared/api";

type HeaderValues = Omit<PostingTemplateFormValues, "lines">;

const EMPTY_HEADER: HeaderValues = {
  productCodeId: "",
  providerId: "",
  version: 1,
  effectiveFrom: "",
  effectiveTo: "",
};

const newLine = (sequence: number): PostingTemplateLineDto => ({
  sequence,
  accountRole: "CUSTOMER_WALLET",
  accountId: "",
  direction: "DEBIT",
  amountBasis: "PRINCIPAL",
});

const toPayload = (
  header: HeaderValues,
  lines: PostingTemplateLineDto[]
): PostingTemplateDto => ({
  productCodeId: header.productCodeId?.trim() || null,
  providerId: header.providerId?.trim() || null,
  version: header.version,
  status: "DRAFT",
  effectiveFrom: header.effectiveFrom,
  effectiveTo: header.effectiveTo?.trim() || null,
  lines: lines.map((line, index) => ({ ...line, sequence: index + 1 })),
});

const LIST_PATH = "/app/ledger/posting-templates";

/**
 * Page dédiée de création d'un modèle d'imputation
 * (`POST /PostingTemplates/add`, guide §6.4) — rôles LEDGER_ADMIN
 * (flashpay-superadmin / flashpay-admin). Le modèle naît en DRAFT ; au
 * moins une ligne est exigée. Preview `ShareablePreview` avant submit.
 *
 * Remplace l'ancienne dialog (trop dense pour un modal) : header fields +
 * repeater de lignes gagnent en lisibilité en pleine page.
 */
export function PostingTemplateCreatePage() {
  const navigate = useNavigate();
  const { t } = useLedgerTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createPostingTemplateSchema(), [i18n.language]);

  const create = useCreatePostingTemplate();
  const [preview, setPreview] = useState<PostingTemplateDto | null>(null);
  const [lines, setLines] = useState<PostingTemplateLineDto[]>([newLine(1)]);
  const [linesError, setLinesError] = useState<string | null>(null);
  const { options: productCodeOptions, isLoading: productCodesLoading } =
    useProductCodeSelectOptions(true);
  const { options: providerOptions, isLoading: providersLoading } =
    useProviderSelectOptions(true);
  const { options: accountOptions, isLoading: accountsLoading } =
    useAccountSelectOptions(true);

  const form = useForm({
    defaultValues: EMPTY_HEADER,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse({ ...value, lines });
      if (!parsed.success) {
        const issueOnLines = parsed.error.issues.some((issue) =>
          issue.path.includes("lines")
        );
        setLinesError(
          issueOnLines ? (parsed.error.issues[0]?.message ?? null) : null
        );
        return;
      }
      setLinesError(null);
      setPreview(toPayload(parsed.data, parsed.data.lines));
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    create.mutate(preview, {
      onSuccess: () => {
        notifSilence(t("ledger.templates.feedback.created"));
        setPreview(null);
        navigate(LIST_PATH);
      },
      onError: (error) => notifSilenceError(error.message),
    });
  };

  const updateLine = <K extends keyof PostingTemplateLineDto>(
    index: number,
    key: K,
    value: PostingTemplateLineDto[K]
  ) =>
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, [key]: value } : line))
    );

  const fieldError =
    (name: keyof HeaderValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <PageBackButton to={LIST_PATH} label={t("common.back")} />
        <h1 className="text-2xl font-semibold text-foreground">
          {t("ledger.templates.create")}
        </h1>
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <form.Field
                name="productCodeId"
                validators={{ onChange: fieldError("productCodeId") }}
              >
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="productCodeId">
                      {t("ledger.templates.form.productCodeId")}
                    </Label>
                    <EntitySearchableSelect
                      id="productCodeId"
                      options={productCodeOptions}
                      value={field.state.value ?? ""}
                      onValueChange={(value) => field.handleChange(value)}
                      placeholder={t(
                        "ledger.templates.form.productCodeIdPlaceholder"
                      )}
                      isLoading={productCodesLoading}
                      clearable
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field
                name="providerId"
                validators={{ onChange: fieldError("providerId") }}
              >
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="providerId">
                      {t("ledger.templates.form.providerId")}
                    </Label>
                    <EntitySearchableSelect
                      id="providerId"
                      options={providerOptions}
                      value={field.state.value ?? ""}
                      onValueChange={(value) => field.handleChange(value)}
                      placeholder={t(
                        "ledger.templates.form.providerIdPlaceholder"
                      )}
                      isLoading={providersLoading}
                      clearable
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field
                name="version"
                validators={{ onChange: fieldError("version") }}
              >
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="version">
                      {t("ledger.templates.form.version")}
                    </Label>
                    <Input
                      id="version"
                      type="number"
                      min={1}
                      step={1}
                      className="notranslate"
                      placeholder="1"
                      value={field.state.value ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(e) =>
                        field.handleChange(e.target.valueAsNumber)
                      }
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field
                name="effectiveFrom"
                validators={{ onChange: fieldError("effectiveFrom") }}
              >
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="effectiveFrom">
                      {t("ledger.templates.form.effectiveFrom")}
                    </Label>
                    <Input
                      id="effectiveFrom"
                      type="date"
                      className="notranslate"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field name="effectiveTo">
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="effectiveTo">
                      {t("ledger.templates.form.effectiveTo")}
                    </Label>
                    <Input
                      id="effectiveTo"
                      type="date"
                      className="notranslate"
                      value={field.state.value ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">
              {t("ledger.templates.form.lines")}
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setLines((prev) => [...prev, newLine(prev.length + 1)])
              }
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("ledger.templates.form.addLine")}
            </Button>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {lines.map((line, index) => (
              <div
                key={index}
                className="grid grid-cols-1 items-end gap-3 rounded-lg border border-border p-3 sm:grid-cols-[1fr_2fr_1fr_1fr_auto]"
              >
                <div className="space-y-1.5">
                  <Label>{t("ledger.templates.form.accountRole")}</Label>
                  <Select
                    value={line.accountRole}
                    onValueChange={(v) =>
                      updateLine(
                        index,
                        "accountRole",
                        v as PostingTemplateLineDto["accountRole"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    {/* Dropdown doit afficher en entier les libellés d'enum
                        (CUSTOMER_WALLET, PROVIDER_SETTLEMENT, …) qui dépassent
                        souvent la largeur de la colonne : min-w = trigger,
                        w-max pour grandir avec le contenu, cap viewport. */}
                    <SelectContent className="w-max min-w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-1rem)]">
                      {ACCOUNT_ROLES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("ledger.templates.form.accountId")}</Label>
                  <EntitySearchableSelect
                    options={accountOptions}
                    value={line.accountId}
                    onValueChange={(value) =>
                      updateLine(index, "accountId", value)
                    }
                    placeholder={t(
                      "ledger.templates.form.accountIdPlaceholder"
                    )}
                    isLoading={accountsLoading}
                    popoverWidth="content"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("ledger.templates.form.direction")}</Label>
                  <Select
                    value={line.direction}
                    onValueChange={(v) =>
                      updateLine(
                        index,
                        "direction",
                        v as PostingTemplateLineDto["direction"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POSTING_DIRECTIONS.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("ledger.templates.form.amountBasis")}</Label>
                  <Select
                    value={line.amountBasis}
                    onValueChange={(v) =>
                      updateLine(
                        index,
                        "amountBasis",
                        v as PostingTemplateLineDto["amountBasis"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AMOUNT_BASIS.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={lines.length <= 1}
                  onClick={() =>
                    setLines((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}

            {linesError ? (
              <p className="text-sm text-destructive">{linesError}</p>
            ) : null}
          </CardContent>
        </Card>

        <div className="sticky bottom-0 z-10 -mx-1 flex flex-col-reverse gap-2 border-t bg-background/95 px-1 py-3 backdrop-blur sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(LIST_PATH)}
            disabled={create.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? <ButtonSpinner /> : null}
            {t("common.confirm")}
          </Button>
        </div>
      </form>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={t("ledger.templates.preview.createTitle")}
          description={t("ledger.templates.preview.createDescription")}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !create.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: create.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </section>
  );
}
